import { FC } from 'react';
import { Link } from '@tanstack/react-router';
import { usePathContext } from '@new/common/hooks/usePathContext';
import { buildObjectivePath } from '@new/common/lib/buildPaths';
import { CheckCircle2, Circle } from 'lucide-react';
import {
  useGetLessonObjectivesQuery,
  useMarkObjectiveCompletedMutation,
} from '@teachur/http-client';
import { Loader } from '@new/common/ui/Loader/Loader';
import { Can } from '@new/casl';
import { EmptyState } from '@new/common/ui/EmptyState/EmptyState';
import { cn } from '@new/lib';
import { useRequireSubscribed } from '@new/features/subscription/hooks/useRequireSubscribed';

interface LessonObjectivesProps {
  lessonId: number;
  courseId?: number;
  courseStatus: 'notStarted' | 'inProgress' | 'completed';
  isPaused: boolean;
  isExamPassed?: boolean;
  isQuizCompleted?: boolean;
  disableNavigation?: boolean;
}

export const LessonObjectives: FC<LessonObjectivesProps> = ({
  lessonId,
  courseId,
  courseStatus,
  isPaused,
  isExamPassed = false,
  disableNavigation = false,
}) => {
  const { degreeId } = usePathContext();
  const { data: objectives, isLoading } = useGetLessonObjectivesQuery(lessonId);
  const { mutate: markCompleted } = useMarkObjectiveCompletedMutation(lessonId, courseId);
  const requireSubscribed = useRequireSubscribed();

  const isObjectivesDisabled = courseStatus === 'notStarted' || isPaused || isExamPassed;
  const isInteractionDisabled = disableNavigation || isObjectivesDisabled;

  const handleObjectiveClick = requireSubscribed((objectiveId: number, isCompleted: boolean) => {
    if (isInteractionDisabled) return;

    markCompleted({ objectiveId, isCompleted });
  });

  if (isLoading) {
    return <Loader />;
  }

  if (!objectives?.length) {
    return <EmptyState message="No objectives found for this lesson." />;
  }

  return (
    <div className="flex flex-col gap-2">
      <h4 className="[font-size:var(--fs-p5)]! text-(--neutral-black-NB1000)! font-semibold m-0!">
        Objectives
      </h4>
      <div className="flex flex-col">
        {objectives.map((objective, index) => {
          const objectivePath = buildObjectivePath({ degreeId, courseId, lessonId }, objective.id);

          const textClassName = cn(
            '[font-size:var(--fs-l5)]! font-medium m-0!',
            objective.isCompleted
              ? 'text-(--neutral-black-NB700)!'
              : disableNavigation
                ? 'text-(--neutral-black-NB1000)'
                : 'text-(--secondary-B1100)!'
          );

          return (
            <div
              key={objective.id}
              className={cn(
                'flex items-center gap-2 p-3 bg-(--neutral-white-NW900) rounded-lg transition-colors',
                isInteractionDisabled
                  ? 'opacity-60'
                  : 'hover:bg-(--neutral-black-NB100) cursor-pointer'
              )}
              onClick={() => handleObjectiveClick(objective.id, objective.isCompleted)}
            >
              {!isInteractionDisabled && (
                <Can I="start" a="Course">
                  {objective.isCompleted ? (
                    <CheckCircle2
                      size={20}
                      className="text-(--neutral-white-NW1000) mt-0.5 shrink-0"
                      fill="var(--success-S700)"
                    />
                  ) : (
                    <Circle size={20} className="text-(--neutral-black-NB400) mt-0.5 shrink-0" />
                  )}
                </Can>
              )}

              <div className="flex leading-tight">
                {disableNavigation ? (
                  <span className={textClassName}>
                    {index + 1}. {objective.name}
                  </span>
                ) : (
                  <Link
                    to={objectivePath}
                    onClick={(event) => event.stopPropagation()}
                    className={textClassName}
                  >
                    {index + 1}. {objective.name}
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
